import type { DoorGuest } from '../types/Door';
import type { SeatingTable } from '../types/Seating';

const lower = (s: string | null | undefined): string => (s ?? '').toLowerCase();

/** Tables a guest of this event can sit at: project-wide tables and this event's own. New array, by number. */
export function usableTables(tables: readonly SeatingTable[], eventId: string): SeatingTable[] {
  const ev = lower(eventId);
  return tables
    .filter((t) => t.event_id == null || lower(t.event_id) === ev)
    .slice()
    .sort((a, b) => (a.number ?? Number.MAX_SAFE_INTEGER) - (b.number ?? Number.MAX_SAFE_INTEGER) || a.id.localeCompare(b.id));
}

export function tableLabel(t: Pick<SeatingTable, 'number' | 'name'>): string {
  if (t.number != null) return t.name ? `Table ${t.number}, ${t.name}` : `Table ${t.number}`;
  return t.name || 'Table';
}

export interface TableFill {
  seated: number;
  capacity: number | null;
  full: boolean;
  /** How many seated beyond capacity. */
  over: number;
  /** Seats left; null when the table has no limit. */
  free: number | null;
}

export function fillOf(t: Pick<SeatingTable, 'capacity'>, seated: number): TableFill {
  const capacity = t.capacity;
  if (capacity == null) return { seated, capacity: null, full: false, over: 0, free: null };
  return { seated, capacity, full: seated >= capacity, over: Math.max(0, seated - capacity), free: Math.max(0, capacity - seated) };
}

export function tableOptionLabel(t: SeatingTable, seated: number): string {
  const fill = fillOf(t, seated);
  if (fill.capacity == null) return `${tableLabel(t)} (${seated} seated)`;
  return `${tableLabel(t)} (${seated}/${fill.capacity}${fill.full ? ', full' : ''})`;
}

export function seatedCounts(guests: readonly DoorGuest[]): Map<string, number> {
  const out = new Map<string, number>();
  for (const g of guests) if (g.table) out.set(g.table.id, (out.get(g.table.id) ?? 0) + 1);
  return out;
}

export interface TableGroup {
  table: SeatingTable;
  guests: DoorGuest[];
}

const byName = (a: DoorGuest, b: DoorGuest): number => a.name.localeCompare(b.name) || a.id.localeCompare(b.id);

/**
 * The table-side view: every usable table (empty ones too) with its guests, the guests with no table, and any guest whose
 * table is not one of the usable tables (kept visible rather than dropped).
 */
export function groupByTable(
  tables: readonly SeatingTable[], guests: readonly DoorGuest[],
): { groups: TableGroup[]; unseated: DoorGuest[]; unplaced: DoorGuest[] } {
  const known = new Set(tables.map((t) => t.id));
  const groups = tables.map((table) => ({ table, guests: guests.filter((g) => g.table?.id === table.id).sort(byName) }));
  return {
    groups,
    unseated: guests.filter((g) => !g.table).sort(byName),
    unplaced: guests.filter((g) => g.table && !known.has(g.table.id)).sort(byName),
  };
}

/** Does the global search text point at this table (its number, name, section or waiter)? Empty text matches. */
export function tableMatches(t: SeatingTable, waiterName: string | null, q: string): boolean {
  const needle = lower(q.trim());
  if (!needle) return true;
  return [tableLabel(t), String(t.number ?? ''), t.name, t.section, waiterName].some((v) => lower(v).includes(needle));
}
